import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import uniqBy from 'lodash/uniqBy';
import AdditiveFilter
from '@fuww/library/src/ProductFilters/AdditiveFilter';
import CategoryFilter
from '@fuww/library/src/ProductFilters/CategoryFilter';
import ColorFilter
from '@fuww/library/src/ProductFilters/ColorFilter';
import RangeFilter
from '@fuww/library/src/ProductFilters/RangeFilter';
import RadioButtonFilter
from '@fuww/library/src/ProductFilters/RadioButtonFilter';
import { OpenDialogButton }
from '@fuww/library/src/ProductFilters/BrandFilter';
import BrandsDialogWithSelected from './BrandsDialog';
import InView from '../InView';
import updateQueryParameters
from '../../lib/updateQueryParameters';
import selectedBrandsConnectionQuery
from '../../lib/queries/products/selectedBrandsConnectionQuery.graphql';
import messages from '../../lib/messages.json';
import inTest from '../../lib/inTest';
import withQuery from '../../lib/withQuery';
import getBrandsBySelected from '../../lib/getBrandsBySelected';
import productBrandsQuery
from '../../lib/queries/products/filters/productBrands.graphql';
import productDepartmentsQuery
from '../../lib/queries/products/filters/productDepartments.graphql';
import productSizesQuery
from '../../lib/queries/products/filters/productSizes.graphql';
import productColorsQuery
from '../../lib/queries/products/filters/productColors.graphql';
import productSeasonsQuery
from '../../lib/queries/products/filters/productSeasons.graphql';
import productPricesQuery
from '../../lib/queries/products/filters/productPrices.graphql';
import productPrintsQuery
from '../../lib/queries/products/filters/productPrints.graphql';
import productMaterialsQuery
from '../../lib/queries/products/filters/productMaterials.graphql';
import productModelImagesQuery
from '../../lib/queries/products/filters/productModelImages.graphql';
import departmentAgeGroups from './departmentAgeGroups';
import departmentGenders from './departmentGenders';
import replaceRoute from '../../lib/replaceRoute';

const filterDepartments = (data) => (
  Object.keys(departmentGenders)
    .filter((departmentName) => {
      const genders = departmentGenders[departmentName];

      return genders.length === 0 || data.productGenders.includes(
        ...genders,
      );
    })
    .filter((departmentName) => {
      const ageGroups = departmentAgeGroups[departmentName];

      return ageGroups.length === 0 || data.productAgeGroups.includes(
        ...ageGroups,
      );
    })
);

export const CategoryFilterPlaceholder = ({ title }) => (
  <CategoryFilter
    title={title}
    loading
    modal
    categories={
      Array.from({ length: 6 }).map(
        (_, index) => ({
          name: `${index}${'o'.repeat(Math.floor(Math.random() * 4) + 2)}`,
        }),
      )
    }
    name={title}
  />
);

CategoryFilterPlaceholder.propTypes = {
  title: PropTypes.string.isRequired,
};

const AdditiveFilterPlaceholder = ({ title }) => (
  <AdditiveFilter
    title={title}
    loading
    choices={
      Array.from({ length: 6 }).map(
        (_, index) => ({
          id: index,
          name: 'o'.repeat(Math.floor(Math.random() * 4) + 3),
        }),
      )
    }
    selected={[]}
  />
);

AdditiveFilterPlaceholder.propTypes = {
  title: PropTypes.string.isRequired,
};

const RangeFilterPlaceholder = ({ title }) => (
  <RangeFilter
    title={title}
    min={20}
    max={102}
    loading
    value={[20, 102]}
    valueLabelDisplay="on"
  />
);

RangeFilterPlaceholder.propTypes = {
  title: PropTypes.string.isRequired,
};

const DepartmentFilter = ({
  loading,
  queryParams,
  data,
}) => {
  const intl = useIntl();
  const { route: currentRouteWithSlash } = useRouter();

  const currentRoute = currentRouteWithSlash.slice(1);
  const departments = filterDepartments(data);

  return (
    departments.length > 0 && (
      <CategoryFilter
        title={intl.formatMessage(messages['products.department'])}
        categories={
          [
            {
              name: 'All',
              handleInteraction: () => {
                const { department: _department, ...rest } = queryParams;

                replaceRoute(currentRoute, rest);
              },
            },
            ...departments.map((name) => ({
              name,
              handleInteraction: () => {
                replaceRoute(
                  currentRoute,
                  {
                    ...queryParams,
                    department: name,
                  },
                );
              },
            })),
          ]
        }
        selectedCategory={queryParams.department ?? 'All'}
        loading={loading}
        modal
        name={intl.formatMessage(messages['products.department'])}
      />
    )
  );
};

DepartmentFilter.propTypes = {
  data: PropTypes.shape({
    productAgeGroups: PropTypes.arrayOf(
      PropTypes.string,
    ),
    productGenders: PropTypes.arrayOf(
      PropTypes.string,
    ),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  queryParams: PropTypes.shape({
    department: PropTypes.string,
  }).isRequired,
};

const DepartmentFilterWithQuery = withQuery(DepartmentFilter);

const BrandsDialogWithSelectedQuery = withQuery(BrandsDialogWithSelected);
const BRANDLIMIT = 100;

const BrandsFilter = ({
  data,
  loading,
  variables,
  queryParams,
}) => {
  const intl = useIntl();
  const [brandDialogOpen, setBrandDialogOpen] = useState(false);

  return (
    <div>
      {data.productBrands.length > 0 && (
        <>
          <AdditiveFilter
            title={intl.formatMessage(messages['products.brands'])}
            choices={
              getBrandsBySelected(
                variables,
                uniqBy(
                  [...data.brands, ...data.productBrands], ({ id }) => id,
                ).slice(0, 20),
                queryParams,
              )
            }
            selected={variables.brandIds}
            loading={loading}
          >
            {data.productBrands.length > 20
            && (
              <OpenDialogButton
                onClick={() => { setBrandDialogOpen(true); }}
                variant="text"
              >
                {`${intl.formatMessage(messages['products.moreBrands'])}`}
              </OpenDialogButton>
            )}
          </AdditiveFilter>
          {brandDialogOpen && (
            <BrandsDialogWithSelectedQuery
              query={selectedBrandsConnectionQuery}
              errorMessage="Error loading more brands."
              variables={{ ...variables, first: BRANDLIMIT }}
              queryParams={queryParams}
              closeDialog={() => { setBrandDialogOpen(false); }}
              showLoaderWhenSetVariables={false}
            />
          )}
        </>
      )}
    </div>
  );
};

BrandsFilter.propTypes = {
  data: PropTypes.shape({
    productBrands: PropTypes.arrayOf(
      PropTypes.shape({}),
    ),
    brands: PropTypes.arrayOf(
      PropTypes.shape({}),
    ),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  queryParams: PropTypes.shape({
    department: PropTypes.string,
  }).isRequired,
  variables: PropTypes.shape({
    brandIds: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
};

const BrandsFilterWithQuery = withQuery(BrandsFilter);

const PriceFilter = ({
  data,
  loading,
  variables,
  queryParams,
}) => {
  const intl = useIntl();
  const { route: currentRouteWithSlash } = useRouter();

  const currentRoute = currentRouteWithSlash.slice(1);

  const [prices, setPrices] = React.useState([
    variables.priceAtLeast || 0,
    variables.priceAtMost || data.productMaximumPriceRange,
  ]);

  useEffect(() => {
    setPrices([
      variables.priceAtLeast || 0,
      variables.priceAtMost || data.productMaximumPriceRange,
    ]);
  }, [variables, data.productMaximumPriceRange]);

  const onPricesChange = (event, [newMin, newMax]) => {
    setPrices([newMin, newMax]);
  };

  return (
    <div>
      {data.productMaximumPriceRange > 0 && (
        <RangeFilter
          title={intl.formatMessage(messages['products.price'])}
          data-testid="price-range-slider"
          loading={loading}
          max={data.productMaximumPriceRange}
          min={0}
          value={prices}
          valueLabelDisplay="on"
          step={1}
          showClear={Boolean(queryParams.minPrice || queryParams.maxPrice)}
          onChange={onPricesChange}
          onChangeCommitted={
            (event, value) => {
              const {
                minPrice: _minPrice,
                maxPrice: _maxPrice,
                ...rest
              } = queryParams;

              replaceRoute(
                currentRoute,
                {
                  ...rest,
                  ...(value[0] === 0 ? {} : { minPrice: value[0] }),
                  ...(value[1] === data.productMaximumPriceRange
                    ? {} : { maxPrice: value[1] }),
                },
              );
            }
          }
          onClear={() => {
            const {
              minPrice: _minPrice,
              maxPrice: _maxPrice,
              ...rest
            } = queryParams;

            replaceRoute(currentRoute, rest);
          }}
        />
      )}
    </div>
  );
};

PriceFilter.propTypes = {
  loading: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    productMaximumPriceRange: PropTypes.number,
  }).isRequired,
  queryParams: PropTypes.shape({
    minPrice: PropTypes.string,
    maxPrice: PropTypes.string,
  }).isRequired,
  variables: PropTypes.shape({
    priceAtLeast: PropTypes.number,
    priceAtMost: PropTypes.number,
  }).isRequired,
};

const PriceFilterWithQuery = withQuery(PriceFilter);

const SizesFilter = ({
  data,
  loading,
  variables,
  queryParams,
}) => {
  const intl = useIntl();
  const { route: currentRouteWithSlash } = useRouter();

  const currentRoute = currentRouteWithSlash.slice(1);

  return (
    <div>
      {data.productSizes.length > 0 && (
      <AdditiveFilter
        loading={loading}
        title={`${intl.formatMessage(messages['products.sizes'])}`}
        choices={data.productSizes.map((size) => ({
          name: size,
          id: size,
          onChange: (event) => {
            replaceRoute(
              currentRoute,
              updateQueryParameters(
                queryParams,
                variables,
                'sizes',
                size,
                event.target.checked,
              ),
            );
          },
        }))}
        selected={variables.sizes}
      />
      )}
    </div>
  );
};

SizesFilter.propTypes = {
  loading: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    productSizes: PropTypes.arrayOf(
      PropTypes.string,
    ),
  }).isRequired,
  queryParams: PropTypes.shape({}).isRequired,
  variables: PropTypes.shape({
    sizes: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

const SizesFilterWithQuery = withQuery(SizesFilter);

const SeasonsFilter = ({
  data,
  loading,
  variables,
  queryParams,
}) => {
  const intl = useIntl();
  const { route: currentRouteWithSlash } = useRouter();

  const currentRoute = currentRouteWithSlash.slice(1);

  return (
    <div>
      {data.productSeasons.length > 0 && (
      <AdditiveFilter
        title={`${intl.formatMessage(messages['products.seasons'])}`}
        loading={loading}
        choices={data.productSeasons.map((season) => ({
          name: season,
          id: season,
          onChange: (event) => {
            replaceRoute(
              currentRoute,
              updateQueryParameters(
                queryParams,
                variables,
                'seasons',
                season,
                event.target.checked,
              ),
            );
          },
        }))}
        selected={variables.seasons}
      />
      )}
    </div>
  );
};

SeasonsFilter.propTypes = {
  loading: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    productSeasons: PropTypes.arrayOf(
      PropTypes.string,
    ),
  }).isRequired,
  queryParams: PropTypes.shape({}).isRequired,
  variables: PropTypes.shape({
    seasons: PropTypes.arrayOf(
      PropTypes.string,
    ),
  }).isRequired,
};

const SeasonsFilterWithQuery = withQuery(SeasonsFilter);

const ColorsFilter = ({
  data,
  loading,
  variables,
  queryParams,
}) => {
  const intl = useIntl();
  const { route: currentRouteWithSlash } = useRouter();

  const currentRoute = currentRouteWithSlash.slice(1);

  return (
    <div>
      {data.productColors.length > 0 && (
      <ColorFilter
        title={intl.formatMessage(messages['products.colors'])}
        loading={loading}
        choices={data.productColors.map(({ name, id }) => ({
          name,
          id,
          onChange: (event) => {
            replaceRoute(
              currentRoute,
              updateQueryParameters(
                queryParams,
                variables,
                'colorIds',
                id,
                event.target.checked,
              ),
            );
          },
        }))}
        selected={variables.colorIds}
      />
      )}
    </div>
  );
};

ColorsFilter.propTypes = {
  loading: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    productColors: PropTypes.arrayOf(
      PropTypes.shape({}),
    ),
  }).isRequired,
  queryParams: PropTypes.shape({}).isRequired,
  variables: PropTypes.shape({
    colorIds: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
};

const ColorsFilterWithQuery = withQuery(ColorsFilter);

const PrintsFilter = ({
  data,
  loading,
  variables,
  queryParams,
}) => {
  const intl = useIntl();
  const { route: currentRouteWithSlash } = useRouter();

  const currentRoute = currentRouteWithSlash.slice(1);

  return (
    <div>
      {data.productPrints.length > 0 && (
      <AdditiveFilter
        title={intl.formatMessage(messages['products.prints'])}
        loading={loading}
        choices={data.productPrints.map(({ name, id }) => ({
          name,
          id,
          onChange: (event) => {
            replaceRoute(
              currentRoute,
              updateQueryParameters(
                queryParams,
                variables,
                'printIds',
                id,
                event.target.checked,
              ),
            );
          },
        }))}
        selected={variables.printIds}
      />
      )}
    </div>
  );
};

PrintsFilter.propTypes = {
  loading: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    productPrints: PropTypes.arrayOf(
      PropTypes.shape({}),
    ),
  }).isRequired,
  queryParams: PropTypes.shape({}).isRequired,
  variables: PropTypes.shape({
    printIds: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
};

const PrintsFilterWithQuery = withQuery(PrintsFilter);

const MaterialsFilter = ({
  data,
  loading,
  variables,
  queryParams,
}) => {
  const intl = useIntl();
  const { route: currentRouteWithSlash } = useRouter();

  const currentRoute = currentRouteWithSlash.slice(1);

  return (
    <div>
      { loading }
      {data.productMaterials.length > 0 && (
      <AdditiveFilter
        title={intl.formatMessage(messages['products.materials'])}
        loading={loading}
        choices={data.productMaterials.map(({ name, id }) => ({
          name,
          id,
          onChange: (event) => {
            replaceRoute(
              currentRoute,
              updateQueryParameters(
                queryParams,
                variables,
                'materialIds',
                id,
                event.target.checked,
              ),
            );
          },
        }))}
        selected={variables.materialIds}
      />
      )}
    </div>
  );
};

MaterialsFilter.propTypes = {
  loading: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    productMaterials: PropTypes.arrayOf(
      PropTypes.shape({}),
    ),
  }).isRequired,
  queryParams: PropTypes.shape({}).isRequired,
  variables: PropTypes.shape({
    materialIds: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
};

const MaterialsFilterWithQuery = withQuery(MaterialsFilter);

const ModelFilter = ({
  data,
  loading,
  variables,
  queryParams,
}) => {
  const intl = useIntl();
  const { route: currentRouteWithSlash } = useRouter();

  const currentRoute = currentRouteWithSlash.slice(1);

  return (
    <div>
      {data.productModelImages.length > 0
      && (
      <RadioButtonFilter
        title={intl.formatMessage(messages['products.modelImages'])}
        loading={loading}
        name="withImage"
        values={[
          {
            value: '',
            label: intl.formatMessage(messages['products.showAll']),
          },
          {
            value: 'true',
            label: intl.formatMessage(messages['products.imageWithModel']),
          },
          {
            value: 'false',
            label: intl.formatMessage(messages['products.imageWithoutModel']),
          },
        ]}
        value={
          typeof variables.model === 'boolean' ? variables.model.toString() : ''
        }
        onChange={
            (event) => {
              const { value } = event.target;
              const { model: _model, ...rest } = queryParams;

              replaceRoute(
                currentRoute,
                {
                  ...rest,
                  ...(value ? { model: value } : {}),
                },
              );
            }
          }
      />
      )}
    </div>
  );
};

ModelFilter.propTypes = {
  loading: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    productModelImages: PropTypes.arrayOf(
      PropTypes.bool,
    ),
  }).isRequired,
  queryParams: PropTypes.shape({
    model: PropTypes.string,
  }).isRequired,
  variables: PropTypes.shape({
    model: PropTypes.bool,
  }).isRequired,
};

const ModelFilterWithQuery = withQuery(ModelFilter);

const FilterPanel = ({
  variables,
  queryParams,
}) => {
  const intl = useIntl();

  return (
    <div>
      <InView>
        <SeasonsFilterWithQuery
          query={productSeasonsQuery}
          variables={variables}
          showLoaderWhenSetVariables={false}
          errorMessage="Error loading product seasons."
          queryParams={queryParams}
          ssr={inTest}
          loader={(
            <AdditiveFilterPlaceholder
              title={intl.formatMessage(messages['products.seasons'])}
            />
          )}
        />
      </InView>
      <InView>
        <DepartmentFilterWithQuery
          query={productDepartmentsQuery}
          variables={variables}
          showLoaderWhenSetVariables={false}
          errorMessage="Error loading departments."
          queryParams={queryParams}
          ssr={inTest}
          loader={(
            <CategoryFilterPlaceholder
              title={intl.formatMessage(messages['products.department'])}
            />
          )}
        />
      </InView>
      <InView>
        { variables.brandIdsScope.length === 0 && (
          <BrandsFilterWithQuery
            query={productBrandsQuery}
            variables={variables}
            showLoaderWhenSetVariables={false}
            errorMessage="Error loading brands."
            queryParams={queryParams}
            ssr={inTest}
            loader={(
              <AdditiveFilterPlaceholder
                title={intl.formatMessage(messages['products.brands'])}
              />
            )}
          />
        )}
      </InView>
      <InView>
        <PriceFilterWithQuery
          query={productPricesQuery}
          variables={variables}
          showLoaderWhenSetVariables={false}
          errorMessage="Error loading price range."
          queryParams={queryParams}
          ssr={inTest}
          loader={(
            <RangeFilterPlaceholder
              title={intl.formatMessage(messages['products.price'])}
            />
          )}
        />
      </InView>
      <InView>
        <ColorsFilterWithQuery
          query={productColorsQuery}
          variables={variables}
          showLoaderWhenSetVariables={false}
          errorMessage="Error loading product colors."
          queryParams={queryParams}
          ssr={inTest}
          loader={(
            <AdditiveFilterPlaceholder
              title={intl.formatMessage(messages['products.colors'])}
            />
          )}
        />
      </InView>
      <InView>
        <PrintsFilterWithQuery
          query={productPrintsQuery}
          variables={variables}
          showLoaderWhenSetVariables={false}
          errorMessage="Error loading product prints."
          queryParams={queryParams}
          ssr={inTest}
          loader={(
            <AdditiveFilterPlaceholder
              title={intl.formatMessage(messages['products.prints'])}
            />
          )}
        />
      </InView>
      <InView>
        <MaterialsFilterWithQuery
          query={productMaterialsQuery}
          variables={variables}
          showLoaderWhenSetVariables={false}
          errorMessage="Error loading product materials."
          queryParams={queryParams}
          ssr={inTest}
          loader={(
            <AdditiveFilterPlaceholder
              title={intl.formatMessage(messages['products.materials'])}
            />
          )}
        />
      </InView>
      <InView>
        <ModelFilterWithQuery
          query={productModelImagesQuery}
          variables={variables}
          showLoaderWhenSetVariables={false}
          errorMessage="Error loading product model images"
          queryParams={queryParams}
          ssr={inTest}
          loader={(
            <AdditiveFilterPlaceholder
              title={intl.formatMessage(messages['products.modelImages'])}
            />
          )}
        />
      </InView>
      <InView>
        {variables.mainCategories.length > 0 && (
          <SizesFilterWithQuery
            query={productSizesQuery}
            variables={variables}
            showLoaderWhenSetVariables={false}
            errorMessage="Error loading product sizes."
            queryParams={queryParams}
            ssr={inTest}
            loader={(
              <AdditiveFilterPlaceholder
                title={intl.formatMessage(messages['products.sizes'])}
              />
            )}
          />
        )}
      </InView>
    </div>
  );
};

FilterPanel.propTypes = {
  queryParams: PropTypes.shape({
    department: PropTypes.string,
  }).isRequired,
  variables: PropTypes.shape({
    brandIds: PropTypes.arrayOf(PropTypes.number),
    colorIds: PropTypes.arrayOf(PropTypes.number),
    sizes: PropTypes.arrayOf(PropTypes.string),
    seasons: PropTypes.arrayOf(PropTypes.string),
    genders: PropTypes.arrayOf(PropTypes.string),
    ageGroups: PropTypes.arrayOf(PropTypes.string),
    priceAtLeast: PropTypes.number,
    priceAtMost: PropTypes.number,
    brandIdsScope: PropTypes.arrayOf(PropTypes.number),
    mainCategories: PropTypes.arrayOf(PropTypes.string),
    model: PropTypes.bool,
  }).isRequired,
};

export default FilterPanel;
